import { useState } from "react";
import cn from "classnames";
import { DistributionLocation, SelectedRoute } from "module/eddm/dto";
import { getRouteResidences } from "utils/eddm";
import { usePostHog } from "posthog-js/react";
import { EmitAnalyticsEvent } from "module/analytics/application/EmitAnalyticsEvent";
import { formatNumber, toFixed2 } from "utils/formators";
import { GoogleMap } from "@react-google-maps/api";
import { ExpandIcon } from "components/icons/components/Expand";
import { RouteIcon } from "components/icons/components/RouteIcon";
import { ResidencesIcon } from "components/icons/components/ResidencesIcon";
import { CollapseIcon } from "components/icons/components/Collapse";
import { Tooltip } from "@material-ui/core";
import { DeleteIcon } from "static/media/delete-icon";
import { useStore } from "store";
import { RouteCard } from "./RouteCard";
import { AddressIconSelected, AddressIconUnselected } from "./icons";

interface AddressCardProps {
  address: DistributionLocation;
  onClick: () => void;
  handleDeleteLocation: (deletedLocation: DistributionLocation) => void;
  setSelectedRoutes: (selectedRoutes: SelectedRoute[] | ((selectedRoutes: SelectedRoute[]) => SelectedRoute[])) => void;
  googleMap: GoogleMap | null;
  setShouldAutoSave: (shouldAutoSave: boolean) => void;
  setIsUndoOrRedo: (isUndoOrRedo: boolean) => void;
  setIsSingleRouteSelection: (isSingleSelection: boolean) => void;
}

export const AddressCardV2 = ({
  address,
  onClick,
  handleDeleteLocation,
  setSelectedRoutes,
  googleMap,
  setShouldAutoSave,
  setIsUndoOrRedo,
  setIsSingleRouteSelection,
}: AddressCardProps) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const { isFocused, name, selectedRoutes } = address;
  const [expanded, setExpanded] = useState(isFocused);
  const {
    user: { id: userId },
    client: { name: clientName },
    campaign: { isSubmitted },
    costsCalculationData: { costPerFlyer },
  } = useStore();
  const posthog = usePostHog();
  const posthogEventTracker = new EmitAnalyticsEvent(posthog);
  const noRoutes = !selectedRoutes?.length;
  const lightGray = "#AEAFC7";

  const handleRouteDelete = (route: SelectedRoute) => {
    if (!route || !googleMap) return;
    setShouldAutoSave(true);
    setIsUndoOrRedo(false);
    setIsSingleRouteSelection(true);
    setSelectedRoutes((selectedRoutes) => [...selectedRoutes.filter((r) => r.id !== route.id)]);
    // @ts-ignore
    googleMap.data.forEach((feature) => {
      if (route.id === feature.getProperty("id")) {
        feature.setProperty("selected", false);
      }
    });
  };

  const totalAddresses = getRouteResidences(selectedRoutes);
  const labelCls = (extra?: string) =>
    cn("text-xs text-[#707087] dark:text-muted-foreground", extra, {
      "text-[#AEAFC7] dark:text-muted-foreground": noRoutes,
      "!text-red-600": isDeleting && !noRoutes,
    });

  return (
    <div
      data-cy={`addressCard-${name}`}
      className="cb-v2-root relative mb-[25px] mt-2.5 flex flex-col items-center justify-center"
    >
      <div className="mb-[5px] flex min-h-[30px] w-full items-center justify-between">
        <div
          data-cy={`addressCardEdit-${name}`}
          className={cn("flex cursor-pointer items-center text-foreground", isDeleting ? "max-w-[65%]" : "max-w-[90%]")}
          onClick={onClick}
        >
          {isDeleting ? <b>Delete</b> : isFocused ? <AddressIconSelected /> : <AddressIconUnselected />}
          <span className="ml-[5px] overflow-hidden text-ellipsis whitespace-nowrap font-normal">{name}</span>{" "}
          {isDeleting ? "?" : ""}
        </div>
        {isDeleting ? (
          <div className="flex">
            <div
              className="cursor-pointer rounded px-[15px] py-0.5 text-xs font-extrabold text-foreground hover:bg-[#FFC9D3] dark:hover:bg-primary/25"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDeleting(false);
              }}
            >
              No
            </div>
            <div
              className="ml-1 cursor-pointer rounded bg-[#EE4360] px-[15px] py-0.5 text-xs font-extrabold text-white"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                posthogEventTracker.run({
                  eventName: `Deleted entire location`,
                  userId: userId,
                  clientName,
                  device: navigator.userAgent,
                });
                handleDeleteLocation(address);
              }}
            >
              Yes
            </div>
          </div>
        ) : (
          <div className="flex">
            {!!selectedRoutes?.length && (
              <Tooltip
                classes={{ tooltip: "!bg-[#222336] !px-4 !py-2 !text-sm !font-bold", arrow: "!text-[#222336]" }}
                title={expanded ? "Collapse location" : "Expand location"}
                arrow
                placement="top"
              >
                <button
                  className="mr-[5px] flex h-[22px] w-[22px] cursor-pointer items-center justify-center rounded-full border border-solid border-border bg-transparent text-foreground"
                  onClick={() => {
                    posthogEventTracker.run({
                      eventName: `${expanded ? "Collapsed" : "Expanded"} addresses list`,
                      userId: userId,
                      clientName,
                      device: navigator.userAgent,
                    });
                    setExpanded(!expanded);
                  }}
                >
                  {expanded ? <CollapseIcon /> : <ExpandIcon />}{" "}
                </button>
              </Tooltip>
            )}
            <button
              data-cy={`addressCardRemove-${name}`}
              className="flex h-[22px] w-[22px] cursor-pointer items-center justify-center rounded-full border border-solid border-border bg-transparent text-foreground"
            >
              <DeleteIcon
                disabled={isSubmitted}
                width={"16"}
                height={"16"}
                className="cursor-pointer"
                onClick={(e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (isSubmitted) return;
                  setIsDeleting(true);
                }}
              />
            </button>
          </div>
        )}
      </div>
      <div
        className={cn(
          "relative flex w-full items-center justify-between border border-solid border-transparent pb-3 text-sm text-foreground",
          {
            // empty (no routes) — subtle neutral card
            "rounded-lg border-[#F0F0FF] bg-[#FCFCFF] p-3 dark:border-border dark:bg-card": !selectedRoutes?.length,
            // collapsed with routes — pink stacked-cards effect (dark: flat card)
            "rounded-lg border-[#FFD7F4] bg-[#FFF5F9] p-3 shadow-[0px_3px_0px_-1px_#FFF5F9,0px_3px_#FFD7F4,0px_6px_0px_-1px_#FFF5F9,0px_6px_#FFD7F4] dark:border-border dark:bg-popover dark:shadow-none":
              !expanded && !!selectedRoutes?.length,
            // collapsed + deleting — red stacked effect
            "!border-[#E00404] !bg-[#FF798D1F] shadow-[0px_3px_0px_-1px_#F9F9FF,0px_3px_#E00404,0px_6px_0px_-1px_#F9F9FF,0px_6px_#E00404]":
              !expanded && !!selectedRoutes?.length && isDeleting,
          }
        )}
      >
        <div className="flex flex-col">
          <span className={labelCls()}>Routes</span>
          <span
            className={cn("flex items-center text-sm font-medium [&_svg]:mr-0.5", {
              "text-[#AEAFC7] dark:text-muted-foreground": noRoutes,
              "!text-red-600": isDeleting && !noRoutes,
            })}
          >
            <RouteIcon stroke={noRoutes ? lightGray : isDeleting ? "#E00404" : null} /> {selectedRoutes?.length || 0}
          </span>
        </div>
        <div className="flex flex-col">
          <span className={labelCls()}>Residences</span>
          <span
            className={cn("flex items-center text-sm font-medium [&_svg]:mr-0.5", {
              "text-[#AEAFC7] dark:text-muted-foreground": noRoutes,
              "!text-red-600": isDeleting && !noRoutes,
            })}
          >
            <ResidencesIcon stroke={noRoutes ? lightGray : isDeleting ? "#E00404" : null} />{" "}
            {formatNumber(totalAddresses)}
          </span>
        </div>
        <div className="flex flex-col items-end">
          <span className={labelCls("font-semibold")}>Costs</span>
          <span
            className={cn("font-bold", {
              "text-[#AEAFC7] dark:text-muted-foreground": noRoutes,
              "!text-red-600": isDeleting && !noRoutes,
            })}
          >
            $ {formatNumber(toFixed2(costPerFlyer * totalAddresses))}
          </span>
        </div>
      </div>
      {expanded &&
        !!selectedRoutes?.length &&
        selectedRoutes.map((route, i) => (
          <RouteCard
            key={`${route.metadata.ZIP}-${route.metadata.CROUTE}`}
            route={route}
            className={cn({
              "rounded-t-lg": i === 0,
              "!border-t !border-solid !border-[#E00404]": i === 0 && isDeleting,
              "rounded-b-lg": i === selectedRoutes.length - 1,
              "!border-b !border-solid !border-[#E00404]": i === selectedRoutes.length - 1 && isDeleting,
            })}
            isDeleting={isDeleting}
            handleRouteDelete={handleRouteDelete}
            googleMap={googleMap}
          />
        ))}
    </div>
  );
};
